/* Shared plan-gating contract. Single source of truth for which features
   require which plan tier. The POS (Flutter) mirrors this map in Dart.

   Access model (see supabase/subscriptions.sql → my_access()):
     effectiveTier ∈ 'pro' (also during trial) | 'growth' | 'starter' | 'none'(blocked)
*/

export type Tier = 'starter' | 'growth' | 'pro';
export type EffectiveTier = Tier | 'none';

export const TIER_RANK: Record<Tier, number> = { starter: 1, growth: 2, pro: 3 };

/** Does `effective` satisfy the `required` minimum tier? */
export function hasAccess(effective: EffectiveTier, required: Tier): boolean {
  if (effective === 'none') return false;
  return TIER_RANK[effective] >= TIER_RANK[required];
}

/** Minimum tier required for each gated feature key. Anything not listed is
    available on every plan (starter). */
export const FEATURE_TIER: Record<string, Tier> = {
  // ---- Growth ----
  'qr-ordering': 'growth', // Scan & QR table ordering + digital menu
  reservations: 'growth',
  inventory: 'growth',
  customers: 'growth',     // CRM + loyalty
  feedback: 'growth',
  aggregators: 'pro',
  'report-builder': 'growth',
  whatsapp: 'growth',      // settings tab: order updates (dormant)
  loyalty: 'growth',       // settings tab: loyalty config
  website: 'growth',
  captain: 'growth',
  hotel: 'growth',         // Hotel / room-service ordering (StayOS link)
  // ---- Pro ----
  marketing: 'pro',        // WhatsApp marketing campaigns (dormant)
  outlets: 'pro',          // multi-outlet + consolidated reports
  einvoice: 'pro',         // settings tab: e-invoice (dormant)
  'ai-voice': 'pro',
  wallet: 'pro',           // loyalty+ wallet / tiers
  tally: 'pro',            // Tally / accounting export
};

/* -------------------------------------------------------------------------
   Runtime feature flags (superadmin-editable).

   The superadmin panel stores the feature → min-tier matrix + per-tenant
   overrides in the DB (ezydine.feature_flags / tenant_feature_overrides). The
   merchant app fetches them at sign-in via ezydine.my_features() and calls
   applyFeatureFlags() so gating reflects the owner's edits without a rebuild.
   Until that runs, the hardcoded FEATURE_TIER above is the fallback.
------------------------------------------------------------------------- */

export interface RuntimeFeature {
  /** Minimum tier required (null/absent ⇒ always available). */
  minTier: Tier | null;
  /** Global kill switch — false hides the feature for everyone. */
  enabled: boolean;
  /** Per-tenant override: true grants, false revokes, null/undefined = none. */
  override?: boolean | null;
}

let RUNTIME_FLAGS: Record<string, RuntimeFeature> = {};

/** Hydrate the runtime flag store from ezydine.my_features() rows. */
export function applyFeatureFlags(
  rows: { feature: string; minTier: string | null; enabled: boolean; overrideEnabled: boolean | null }[]
): void {
  const next: Record<string, RuntimeFeature> = {};
  for (const r of rows) {
    next[r.feature] = {
      minTier: (r.minTier as Tier) ?? null,
      enabled: r.enabled,
      override: r.overrideEnabled,
    };
  }
  RUNTIME_FLAGS = next;
}

/** Required tier for a feature, runtime-aware (falls back to FEATURE_TIER). */
export function requiredTierFor(feature: string): Tier | null {
  const rt = RUNTIME_FLAGS[feature];
  if (rt) return rt.minTier;
  return FEATURE_TIER[feature] ?? null;
}

/** Master gate: does the caller (with `effective` tier) get this feature? */
export function hasFeature(effective: EffectiveTier, feature: string): boolean {
  const rt = RUNTIME_FLAGS[feature];
  if (rt) {
    if (!rt.enabled) return false;            // global kill switch
    if (rt.override === true) return true;     // per-tenant grant
    if (rt.override === false) return false;   // per-tenant revoke
    return rt.minTier ? hasAccess(effective, rt.minTier) : effective !== 'none';
  }
  const required = FEATURE_TIER[feature];
  if (!required) return effective !== 'none';
  return hasAccess(effective, required);
}

/** Tier required for an /dashboard sidebar path (or null = always available). */
export function tierForPath(path: string): Tier | null {
  const key = path.replace(/^\/dashboard\/?/, '').split('/')[0] || '';
  return requiredTierFor(key);
}

export const PLAN_PRICE: Record<Tier, number> = { starter: 6999, growth: 8999, pro: 11999 };
export const PLAN_LABEL: Record<Tier, string> = { starter: 'Starter', growth: 'Growth', pro: 'Pro' };
